import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const parseLocalized = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'object') return raw.en || raw.ru || Object.values(raw)[0] || '';
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') {
      return obj.en || obj.ru || Object.values(obj)[0] || '';
    }
    return String(raw);
  } catch {
    return String(raw);
  }
};

const allLocales = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') return obj;
  } catch {}
  return null;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const PRIVACY_LABELS = {
  1: { label: 'Public',  color: '#10b981' },
  2: { label: 'Private', color: '#ef4444' },
  3: { label: 'Hidden',  color: '#6b7280' }
};

const ROLE_LABELS = {
  1: 'Member',
  2: 'Admin',
  3: 'Owner'
};

const isUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);

const TeamDetail = () => {
  const { id } = useParams();
  const { fetchWithAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedActivities, setExpandedActivities] = useState(new Set());

  useEffect(() => {
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`/api/teams/${id}`);
      if (!response.ok) throw new Error(`Failed to fetch team: ${response.status}`);
      const json = await response.json();
      setData(json);
      setExpandedActivities(new Set((json.activities || []).map(a => a.id)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActivity = (aid) => {
    const next = new Set(expandedActivities);
    if (next.has(aid)) next.delete(aid); else next.add(aid);
    setExpandedActivities(next);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error)   return <div className="error"><h2>Error</h2><p>{error}</p></div>;
  if (!data)   return null;

  const { team, members, activities, stats } = data;
  const name = parseLocalized(team.name) || `Team #${team.id}`;
  const description = parseLocalized(team.description);
  const nameLocales = allLocales(team.name);
  const descLocales = allLocales(team.description);
  const privacy = PRIVACY_LABELS[team.privacy] || { label: `#${team.privacy}`, color: '#6b7280' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/teams" className="link">← Back to Teams</Link>
      </div>

      {/* Team header card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{
            width: 96, height: 96, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg,#667eea,#764ba2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 36, fontWeight: 700, overflow: 'hidden'
          }}>
            {isUrl(team.image)
              ? <img src={team.image} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (name[0] || '#').toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>{name}</h1>
            {description && (
              <p style={{ color: '#444', marginTop: 8, marginBottom: 12 }}>{description}</p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ background: privacy.color, color: '#fff', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                {privacy.label}
              </span>
              <span style={{ background: '#f3f4f6', color: '#111', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontFamily: 'monospace' }}>
                Invite: {team.invite_code}
              </span>
              <span style={{ color: '#666', fontSize: 13 }}>
                Created {formatDate(team.created_at)}
              </span>
              {team.user_id && (
                <span style={{ color: '#666', fontSize: 13 }}>
                  Owner:{' '}
                  <Link to={`/users/${team.user_id}`} className="link">
                    {team.owner_name || team.owner_email || `User ${team.user_id}`}
                  </Link>
                </span>
              )}
            </div>
            {!isUrl(team.image) && team.image && (
              <div style={{ fontSize: 11, color: '#999', marginTop: 8, fontFamily: 'monospace' }}>
                image_token: {team.image}
              </div>
            )}
          </div>
        </div>

        {/* All locales (debug-style block, helpful for admin) */}
        {(nameLocales || descLocales) && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {nameLocales && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Name (all locales)</div>
                {Object.entries(nameLocales).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 13 }}>
                    <strong style={{ color: '#888' }}>{k}:</strong> {String(v)}
                  </div>
                ))}
              </div>
            )}
            {descLocales && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Description (all locales)</div>
                {Object.entries(descLocales).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 13 }}>
                    <strong style={{ color: '#888' }}>{k}:</strong> {String(v)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Members',   value: stats.member_count,   icon: '👥' },
          { label: 'Activities',value: stats.activity_count, icon: '📰' },
          { label: 'Comments',  value: stats.comment_count,  icon: '💬' },
          { label: 'Likes',     value: stats.like_count,     icon: '❤️' }
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 28 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{s.value}</div>
            <div style={{ color: '#666', fontSize: 13 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Members */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>
          Members ({members.length})
        </h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Meta</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id}>
                  <td>
                    <Link to={`/users/${m.user_id}`} className="link" style={{ fontWeight: 600 }}>
                      {m.user_name || `User ${m.user_id}`}
                    </Link>
                  </td>
                  <td style={{ color: '#666' }}>{m.user_email || '—'}</td>
                  <td>
                    <span style={{
                      background: m.user_id === team.user_id ? '#f59e0b' : '#e5e7eb',
                      color: m.user_id === team.user_id ? '#fff' : '#111',
                      padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600
                    }}>
                      {m.user_id === team.user_id ? 'Owner' : (ROLE_LABELS[m.role] || `Role ${m.role}`)}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: '#666' }}>{formatDate(m.joined_at)}</td>
                  <td style={{ fontSize: 12, color: '#999', fontFamily: 'monospace', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.meta || '—'}
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999', padding: 24 }}>No members</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activities */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            Activities ({activities.length})
          </h2>
          {activities.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }}
                onClick={() => setExpandedActivities(new Set(activities.map(a => a.id)))}>
                Expand all
              </button>
              <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }}
                onClick={() => setExpandedActivities(new Set())}>
                Collapse all
              </button>
            </div>
          )}
        </div>

        {activities.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>No activities yet</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {activities.map(a => {
            const expanded = expandedActivities.has(a.id);
            return (
              <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => toggleActivity(a.id)}
                  style={{
                    padding: 12, cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: expanded ? '#f9fafb' : '#fff'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 16, transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform .15s' }}>▶</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Link
                          to={`/users/${a.user_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="link"
                          style={{ fontWeight: 600 }}
                        >
                          {a.user_name || `User ${a.user_id}`}
                        </Link>
                        <span style={{ color: '#999', fontSize: 13 }}>•</span>
                        <span style={{ color: '#666', fontSize: 13 }}>{formatDate(a.created_at)}</span>
                      </div>
                      {a.content && (
                        <div style={{
                          color: '#333', marginTop: 4,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap'
                        }}>
                          {a.content}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, color: '#666', fontSize: 13, flexShrink: 0, marginLeft: 12 }}>
                    <span>❤️ {a.like_count}</span>
                    <span>💬 {a.comment_count}</span>
                    {a.images.length > 0 && <span>🖼️ {a.images.length}</span>}
                  </div>
                </div>

                {expanded && (
                  <div style={{ padding: 16, background: '#fafafa', borderTop: '1px solid #e5e7eb' }}>
                    {/* Single image on activity */}
                    {a.image && (
                      <div style={{ marginBottom: 12 }}>
                        {isUrl(a.image) ? (
                          <img src={a.image} alt="activity" style={{ maxWidth: 320, maxHeight: 240, borderRadius: 8 }} />
                        ) : (
                          <div style={{ fontSize: 12, color: '#999', fontFamily: 'monospace' }}>image: {a.image}</div>
                        )}
                      </div>
                    )}

                    {/* Multiple ordered images */}
                    {a.images.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {a.images.map(im => (
                          <div key={im.id}>
                            {isUrl(im.image) ? (
                              <img src={im.image} alt="" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6 }} />
                            ) : (
                              <div style={{
                                width: 120, height: 120, borderRadius: 6,
                                background: '#e5e7eb',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, color: '#666', fontFamily: 'monospace',
                                padding: 4, textAlign: 'center', wordBreak: 'break-all'
                              }}>
                                {im.image}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Likes */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                        Likes ({a.likes.length})
                      </div>
                      {a.likes.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#999' }}>No likes yet</div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {a.likes.map(l => (
                            <Link
                              key={l.id}
                              to={`/users/${l.user_id}`}
                              className="link"
                              style={{
                                background: '#fff', border: '1px solid #e5e7eb',
                                padding: '2px 8px', borderRadius: 12, fontSize: 12
                              }}
                            >
                              ❤️ {l.user_name || `User ${l.user_id}`}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Comments */}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                        Comments ({a.comments.length})
                      </div>
                      {a.comments.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#999' }}>No comments yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {a.comments.map(c => (
                            <div key={c.id} style={{
                              background: '#fff', border: '1px solid #e5e7eb',
                              borderRadius: 6, padding: 10
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <Link to={`/users/${c.user_id}`} className="link" style={{ fontWeight: 600, fontSize: 13 }}>
                                  {c.user_name || `User ${c.user_id}`}
                                </Link>
                                <span style={{ fontSize: 12, color: '#999' }}>{formatDate(c.created_at)}</span>
                              </div>
                              <div style={{ fontSize: 14, color: '#222', whiteSpace: 'pre-wrap' }}>{c.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamDetail;
